import {debounce, isEqual} from 'lodash';
import {Component} from 'react';
import {
  Platform,
  StyleSheet,
  Text,
  View,
  LayoutAnimation,
  UIManager,
} from 'react-native';
import {GestureDetector, Gesture} from 'react-native-gesture-handler';
import Slider from '@react-native-assets/slider';
import config from '../../../config';
import FontService from '../../../lib/font';
import unitTypeFormatter from '../../../lib/TripicaUnitTypeFormatter';
import I18n from '../../../lib/i18n';
import {formatValue} from '../../../lib/usage-formatters';
import theme from '../../../theme-config';
import CharacteristicSliderLabel from './CharacteristicSliderLabel';
import CharacteristicSliderPrice from './CharacteristicSliderPrice';
import {hasOnlyFreeOptionChoices} from './lib/hasOnlyFreeOptionChoices';

const IS_WEB = Platform.OS === 'web';

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

class OptionSliderContent extends Component {
  constructor(props) {
    super(props);
    const initialIndex = this._getSelectedIndex(props);
    this.state = {
      index: initialIndex,
      sliderValue: initialIndex,
    };

    this._containerWidth = 0;
    this._gestureStartIndex = 0;

    if (!IS_WEB) {
      this._buildPanGesture(props.disabled);
    }
  }

  // ─── Gesture factory (natif uniquement) ────────────────────────────────────

  _buildPanGesture = disabled => {
    const pan = Gesture.Pan()
      .enabled(!disabled)
      .runOnJS(true)
      .activeOffsetX([-4, 4])
      .failOffsetY([-10, 10])
      .onBegin(() => { this._handlePanBegin(); })
      .onUpdate(e => { this._handlePanUpdate(e.translationX); })
      .onEnd(e => { this._handlePanEnd(e.translationX); })
      .onFinalize((_, success) => { if (!success) this._handlePanCancel(); });

    const tap = Gesture.Tap()
      .enabled(!disabled)
      .runOnJS(true)
      .maxDuration(250)
      .onEnd((e, success) => {
        if (!success) return;
        this._handleTap(e.x);
      });

    this._panGesture = Gesture.Exclusive(pan, tap);
  };

  _handleTap = x => {
    const {options} = this.props;
    if (options.length <= 1 || this._containerWidth === 0) return;
    const raw = Math.max(
      0,
      Math.min(options.length - 1, (x * (options.length - 1)) / this._containerWidth),
    );
    this.onSelectOption(raw);
  };

  _handlePanBegin = () => {
    this._gestureStartIndex = this.state.sliderValue;
  };

  _handlePanUpdate = translationX => {
    const {options} = this.props;
    if (options.length <= 1 || this._containerWidth === 0) return;
    const newIndex = Math.max(
      0,
      Math.min(
        options.length - 1,
        this._gestureStartIndex + (translationX * (options.length - 1)) / this._containerWidth,
      ),
    );
    this.setState({index: newIndex, sliderValue: newIndex});
  };

  _handlePanEnd = translationX => {
    const {options} = this.props;
    const raw =
      this._containerWidth > 0
        ? this._gestureStartIndex +
          (translationX * (options.length - 1)) / this._containerWidth
        : this._gestureStartIndex;
    this.onSelectOption(raw);
  };

  _handlePanCancel = () => {
    const snapped = Math.round(this._gestureStartIndex);
    this.setState({index: snapped, sliderValue: snapped});
  };

  _onContainerLayout = e => {
    this._containerWidth = e.nativeEvent.layout.width;
  };

  // ─── Web: callbacks slider natif (lib gère pan/tap) ────────────────────────

  _onWebValueChange = value => {
    this.setState({index: value, sliderValue: value});
  };
  _debouncedOnWebValueChange = debounce(this._onWebValueChange, 16);

  _onWebSlidingComplete = value => {
    this.onSelectOption(value);
  };

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.selectedOptions, prevProps.selectedOptions)) {
      const idx = this._getSelectedIndex(this.props);
      if (idx !== this.state.sliderValue) {
        this.setState({index: idx, sliderValue: idx});
      }
    }
    if (!IS_WEB && prevProps.disabled !== this.props.disabled) {
      this._buildPanGesture(this.props.disabled);
      this.forceUpdate();
    }
  }

  onSelectOption = index => {
    const anchoredIndex = Math.round(index);
    if (!IS_WEB) {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    }
    this.setState({index: anchoredIndex, sliderValue: anchoredIndex});
    if (!this.props.onSelect) return;

    const previousSelected = this._getSelectedOption();
    const nextSelected = this.props.options[anchoredIndex];
    this.props.onSelect(nextSelected, previousSelected);
  };

  _extractOptionKey = option => {
    if (this.props.optionKeyExtractor) {
      return this.props.optionKeyExtractor(option);
    }
    return option?.ouid;
  };

  _getSelectedIndex = props => {
    const idx = props.options.findIndex(
      opt => opt.ouid != null && props.selectedOptions.some(sel => sel.ouid === opt.ouid),
    );
    return idx !== -1 ? idx : 0;
  };

  _getSelectedOption() {
    const selected = this.props.selectedOptions.find(
      sel => sel.ouid != null && this.props.options.some(opt => opt.ouid === sel.ouid),
    );
    return selected || this.props.options[0];
  }

  formatGraduation = index => {
    if (this.props.name === 'EXTRA') return '';
    const element =
      this.props.options[index]?.characteristic ||
      this.props.options[index]?.characteristics?.[0];
    if (!element) return '';
    const value = element.value;
    if (isNaN(value)) return I18n.t(`VALUE_${value}`);
    if (value == 0) {
      const unit = element.unitType || element.unit;
      if (unit === 'VOLUME') return `0G${I18n.t('unit')}`;
      if (unit === 'DURATION') return `0${I18n.t('time.min')}`;
      return '0';
    }
    return formatValue(value, element.unitType || element.unit);
  };

  _getCharacteristicValue = option => {
    if (option?.characteristics) return option.characteristics[0]?.value;
    return option?.characteristic?.value;
  };

  render() {
    const {
      options,
      outerMargin,
      lightColors,
      showPrice = config.displayOptionPrice,
      showLabel = true,
      showValueBubble = true,
      noIcon = false,
      smallThumb = false,
      smallBubble = false,
      displayIncludedForFirstOptionValue,
    } = this.props;

    const hasOnlyFreeChoices = hasOnlyFreeOptionChoices(options);
    const shouldDisplayWithoutSlider = hasOnlyFreeChoices || options.length === 1;

    const activeColor = lightColors ? theme.overPrimaryColor : theme.slidersColor;
    const currentIndex = Math.round(this.state.index);

    const baseThumbSize = Platform.select({ios: 28, android: 30, default: 28});
    const thumbSize = smallThumb ? 20 : baseThumbSize;
    const halfThumb = thumbSize / 2;

    const showIncludedTextInsteadOfPrice =
      hasOnlyFreeChoices ||
      (displayIncludedForFirstOptionValue && this._getSelectedIndex(this.props) === 0);

    const selectedCharValue = this._getCharacteristicValue(options[currentIndex]);
    const showUnlimitedLabel =
      shouldDisplayWithoutSlider && selectedCharValue === 'UNL';

    const railContent = (
      <View
        style={IS_WEB ? null : {paddingHorizontal: halfThumb}}
        onLayout={this._onContainerLayout}
        testID={this.props.sliderTestID}
      >
        {showValueBubble && (
          <View style={styles.graduationContainer} pointerEvents="none">
            {options.map((_, i) => {
              const isSelected = currentIndex === i;
              return (
                <View key={`grad-${i}`} style={styles.graduationItem}>
                  <View style={[
                    styles.baseLabelContainer,
                    smallBubble && {minWidth: 50, paddingHorizontal: 8},
                    isSelected && {backgroundColor: theme.primaryColor},
                  ]}>
                    <Text
                      numberOfLines={1}
                      style={[
                        styles.graduationLabelText,
                        {color: isSelected ? '#FFFFFF' : activeColor},
                        isSelected ? styles.selectedText : styles.unselectedText,
                      ]}
                    >
                      {this.formatGraduation(i)}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>
        )}

        <View style={styles.sliderAndTicksStack}>
          <View style={styles.ticksOverlay} pointerEvents="none">
            {options.map((_, i) => (
              <View key={`tick-container-${i}`} style={styles.tickAlignmentPoint}>
                <View style={[styles.graduationTick, {backgroundColor: activeColor}]} />
              </View>
            ))}
          </View>

          <Slider
            style={[styles.slider, {height: thumbSize}]}
            testID={unitTypeFormatter(this.props.name)}
            value={this.state.sliderValue}
            minimumValue={0}
            maximumValue={options.length - 1}
            step={IS_WEB ? 0.01 : 1}
            slideOnTap={IS_WEB}
            minimumTrackTintColor={activeColor}
            maximumTrackTintColor={activeColor}
            thumbTintColor={theme.primaryColor}
            trackHeight={6}
            thumbSize={thumbSize}
            enabled={IS_WEB ? !this.props.disabled : false}
            onValueChange={IS_WEB ? this._debouncedOnWebValueChange : undefined}
            onSlidingComplete={IS_WEB ? this._onWebSlidingComplete : undefined}
          />
        </View>
      </View>
    );

    return (
      <View
        style={[
          styles.container,
          {paddingHorizontal: outerMargin},
          this.props.disabled && {opacity: 0.4},
        ]}
        needsOffscreenAlphaCompositing={!IS_WEB && !!this.props.disabled}
      >
        <View style={styles.labelContainer}>
          <View style={styles.labelInline}>
            {showLabel && (
              <CharacteristicSliderLabel
                unitType={this.props.name}
                lightColors={lightColors}
                onPressHelpIcon={this.props.onPressHelpIcon}
                labelOverride={this.props.labelOverride}
                noIcon={noIcon}
                showIncludedTextInsteadOfPrice={showIncludedTextInsteadOfPrice}
              />
            )}
            {this.props.badge}
            {showUnlimitedLabel && (
              <View style={styles.unlimitedLabelContainer}>
                <Text style={styles.unlimitedLabel}>
                  {I18n.t('unlimitedValueWithCaps')}
                </Text>
              </View>
            )}
          </View>
          {showPrice && (
            <CharacteristicSliderPrice
              price={options[currentIndex]?.price?.[this.props.priceKey] || 0}
              currency={this.props.currency}
              lightColors={lightColors}
              showIncludedTextInsteadOfPrice={showIncludedTextInsteadOfPrice}
              periodicity={options[currentIndex]?.periodicity || {}}
            />
          )}
        </View>

        {!shouldDisplayWithoutSlider && (
          <View style={styles.sliderWrapper}>
            {IS_WEB ? (
              railContent
            ) : (
              <GestureDetector gesture={this._panGesture}>
                {railContent}
              </GestureDetector>
            )}
          </View>
        )}
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {paddingBottom: 15, marginHorizontal: 10, marginTop: 10},
  labelContainer: {
    justifyContent: 'space-between',
    flexDirection: 'row',
    marginBottom: 5,
    marginHorizontal: -10,
  },
  labelInline: {flexDirection: 'row', alignItems: 'center'},
  unlimitedLabelContainer: {
    justifyContent: 'center',
    paddingLeft: 12,
  },
  unlimitedLabel: {
    color: theme.secondaryColor,
    fontSize: 16,
    fontWeight: 'normal',
  },
  sliderWrapper: {marginTop: 10},
  sliderAndTicksStack: {
    justifyContent: 'center',
    height: 40,
    position: 'relative',
  },
  slider: {
    width: '100%',
    zIndex: 2,
  },
  ticksOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 1,
    top: 0,
    bottom: 0,
  },
  tickAlignmentPoint: {
    width: 0,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
  graduationTick: {
    width: 14,
    height: 14,
    borderRadius: 7,
  },
  graduationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: 25,
    marginBottom: 5,
  },
  graduationItem: {
    width: 0,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
  baseLabelContainer: {
    height: 25,
    borderRadius: 6,
    paddingHorizontal: 12,
    minWidth: 65,
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  graduationLabelText: {
    fontSize: 15,
    textAlign: 'center',
  },
  selectedText: {
    fontWeight: 'bold',
    ...FontService.getStyle(theme.fontFamily, 'bold'),
  },
  unselectedText: {
    fontWeight: '400',
    ...FontService.getStyle(theme.fontFamily, 'normal'),
  },
});

export default OptionSliderContent;
